import type { StripPlotArea } from "../stripLayout"

// The pointed-at/chosen plan's LETTER above-left of its fan line — the
// strip's version of the desktop fan's hover "PLAN X" end labels (letter
// only: "PLAN S" is desktop-sized, not 76px-strip-sized). The axis-wise
// anchor is only a first guess: a normalization pass then slides the
// label until the glyph box's TRUE nearest distance to the drawn line
// equals the clearance, so the gap reads identical on flat, steep, and
// bending lines.

const PLAN_LETTER_LINE_CLEARANCE_PX = 10
// Room for the letter's own line box below the strip's top padding edge.
const PLAN_LETTER_MIN_BOTTOM_Y_PX = 16
// Page-colored under-stroke (paintOrder) so the letter reads over the
// red dither — the same knock-out treatment as the fan lines' halos.
export const PLAN_LETTER_HALO_PX = 10
// The year pill's estimated bottom-right footprint plus a small gap.
// Plan S is the letter that lands here — its floor-hugging line runs
// exactly behind the pill — and it shifts to the pill's left instead.
const PLAN_LETTER_YEAR_PILL_AVOID_LEFT_PX = 64
const PLAN_LETTER_YEAR_PILL_AVOID_TOP_PX = 36
// Approximate glyph box (the strip's 12px mono bold capitals), hung from
// the textAnchor="end" anchor: x is the right edge, y the baseline —
// also the bottom, capitals having no descender.
const PLAN_LETTER_GLYPH_WIDTH_PX = 7.2
const PLAN_LETTER_GLYPH_HEIGHT_PX = 8.5

type PlanLetterBox = { left: number; right: number; top: number; bottom: number }
type LetterAnchor = { x: number; bottomY: number }
type LetterLayout = { plot: StripPlotArea; avoidYearPill: boolean }

// Anchor position for one plan letter, given its line's screen polyline
// and drawn end point.
export const placePlanLetter = (args: {
  polyline: Array<[number, number]>
  endX: number
  endY: number
  plot: StripPlotArea
  avoidYearPill: boolean
}): { x: number; y: number } => {
  const layout: LetterLayout = { plot: args.plot, avoidYearPill: args.avoidYearPill }
  const anchor = clampToStripAndPill(initialAnchor(args, layout), layout)
  const settled = normalizeClearance(anchor, args.polyline, layout)
  return { x: settled.x, y: settled.bottomY }
}

// Bottom-right corner CLEARANCE px above and left of the line's drawn
// end; the y clamps into the strip for lines that exited the top, and
// the x then re-anchors to where the line crosses the label's bottom
// edge (at that height a steep line sits left of its end point, so the
// end point's own x would put the text on top of the stroke).
const initialAnchor = (
  { polyline, endX, endY }: { polyline: Array<[number, number]>; endX: number; endY: number },
  { plot }: LetterLayout,
): LetterAnchor => {
  const bottomY = Math.min(
    Math.max(
      endY - PLAN_LETTER_LINE_CLEARANCE_PX,
      plot.padding.top + PLAN_LETTER_MIN_BOTTOM_Y_PX,
    ),
    plot.height - plot.padding.bottom,
  )
  const crossX = fanLineXAtY(polyline, bottomY)
  // Clamped to the plot's right edge: the polylines run into the
  // horizontal bleed, and a line still rising at the window edge crosses
  // the label's bottom edge inside the bleed.
  const x = Math.min(
    (crossX ?? endX) - PLAN_LETTER_LINE_CLEARANCE_PX,
    plot.width - plot.padding.right,
  )
  return { x, bottomY }
}

// Keeps the label inside the strip and clear of the year pill: a letter
// whose box lands on the pill's corner shifts to the pill's left.
const clampToStripAndPill = (
  anchor: LetterAnchor,
  { plot, avoidYearPill }: LetterLayout,
): LetterAnchor => {
  const bottomY = Math.min(
    Math.max(anchor.bottomY, plot.padding.top + PLAN_LETTER_MIN_BOTTOM_Y_PX),
    plot.height - plot.padding.bottom,
  )
  let x = Math.min(anchor.x, plot.width - plot.padding.right)
  if (
    avoidYearPill
    && bottomY > plot.height - PLAN_LETTER_YEAR_PILL_AVOID_TOP_PX
    && x > plot.width - PLAN_LETTER_YEAR_PILL_AVOID_LEFT_PX
  ) {
    x = plot.width - PLAN_LETTER_YEAR_PILL_AVOID_LEFT_PX
  }
  return { x, bottomY }
}

// Newton passes: measure the exact glyph-box↔polyline gap and slide
// until the gap IS the clearance, each step sized off a 1px derivative
// probe — the shallow slide-along-the-line cases land in a pass or two
// instead of creeping. Stalls in place when the clamps leave no
// direction that improves the gap.
const normalizeClearance = (
  anchor: LetterAnchor,
  polyline: Array<[number, number]>,
  layout: LetterLayout,
): LetterAnchor => {
  for (let pass = 0; pass < 6; pass++) {
    const step = clearanceStep(anchor, polyline, layout)
    if (step === null) return anchor
    anchor = clampToStripAndPill(
      { x: anchor.x + step.x, bottomY: anchor.bottomY + step.y },
      layout,
    )
  }
  return anchor
}

// One slide step toward gap === clearance; null = settled or stuck.
const clearanceStep = (
  anchor: LetterAnchor,
  polyline: Array<[number, number]>,
  layout: LetterLayout,
): { x: number; y: number } | null => {
  const nearest = nearestBoxToPolyline(letterBoxAt(anchor), polyline)
  if (!Number.isFinite(nearest.distance)) return null
  const shortfall = PLAN_LETTER_LINE_CLEARANCE_PX - nearest.distance
  if (Math.abs(shortfall) < 0.25) return null
  const direction = escapeDirection(nearest, anchor, layout, shortfall)
  if (direction === null) return null
  const probed = nearestBoxToPolyline(
    letterBoxAt({ x: anchor.x + direction.x, bottomY: anchor.bottomY + direction.y }),
    polyline,
  )
  const gainPerPx = probed.distance - nearest.distance
  if (gainPerPx < 0.05) return null
  const stepLength = Math.max(-24, Math.min(24, shortfall / gainPerPx))
  return { x: direction.x * stepLength, y: direction.y * stepLength }
}

// The nearest-pair away direction, with clamp-pinned components dropped
// when moving away (a label pinned at the strip's top escapes a diagonal
// line by sliding along it, not by pushing into the clamp); null = the
// clamps leave no direction at all.
const escapeDirection = (
  nearest: { awayX: number; awayY: number },
  anchor: LetterAnchor,
  { plot, avoidYearPill }: LetterLayout,
  shortfall: number,
): { x: number; y: number } | null => {
  let dirX = nearest.awayX
  let dirY = nearest.awayY
  if (shortfall <= 0) return { x: dirX, y: dirY }
  const pillBinds = avoidYearPill
    && anchor.bottomY > plot.height - PLAN_LETTER_YEAR_PILL_AVOID_TOP_PX
  const rightBound = pillBinds
    ? plot.width - PLAN_LETTER_YEAR_PILL_AVOID_LEFT_PX
    : plot.width - plot.padding.right
  if (
    (dirY < 0 && anchor.bottomY <= plot.padding.top + PLAN_LETTER_MIN_BOTTOM_Y_PX + 0.01)
    || (dirY > 0 && anchor.bottomY >= plot.height - plot.padding.bottom - 0.01)
  ) dirY = 0
  if (dirX > 0 && anchor.x >= rightBound - 0.01) dirX = 0
  const length = Math.hypot(dirX, dirY)
  if (length < 1e-3) return null
  return { x: dirX / length, y: dirY / length }
}

const letterBoxAt = ({ x, bottomY }: LetterAnchor): PlanLetterBox => ({
  left: x - PLAN_LETTER_GLYPH_WIDTH_PX,
  right: x,
  top: bottomY - PLAN_LETTER_GLYPH_HEIGHT_PX,
  bottom: bottomY,
})

// ---- box ↔ polyline geometry ----------------------------------------------

// X where a screen polyline crosses the given y, taking the crossing
// nearest the polyline's end; undefined when it never reaches that height.
const fanLineXAtY = (
  polyline: Array<[number, number]>,
  targetY: number,
): number | undefined => {
  for (let i = polyline.length - 1; i >= 1; i--) {
    const [ax, ay] = polyline[i - 1]
    const [bx, by] = polyline[i]
    if ((ay - targetY) * (by - targetY) <= 0 && ay !== by) {
      return ax + ((targetY - ay) / (by - ay)) * (bx - ax)
    }
  }
  return undefined
}

type NearestPair = {
  distance: number
  boxX: number
  boxY: number
  lineX: number
  lineY: number
}

const OVERLAPPED = { distance: 0, awayX: -Math.SQRT1_2, awayY: -Math.SQRT1_2 }

// Exact nearest gap between an axis-aligned box and a polyline, plus the
// unit direction that moves the box AWAY from its nearest line point
// (up-left — the label's designed side — when an overlap leaves no
// direction). Exact because the minimum is always at a box corner ↔
// segment pair or a segment endpoint ↔ box pair (parallel edge–edge
// minima tie with their endpoints); through-cuts clamp the gap to 0.
const nearestBoxToPolyline = (
  box: PlanLetterBox,
  polyline: Array<[number, number]>,
): { distance: number; awayX: number; awayY: number } => {
  let best: NearestPair = { distance: Number.POSITIVE_INFINITY, boxX: 0, boxY: 0, lineX: 0, lineY: 0 }
  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i]
    const b = polyline[i + 1]
    best = closerPair(best, nearestEndpointToBox(box, a))
    best = closerPair(best, nearestEndpointToBox(box, b))
    for (const corner of boxCorners(box)) {
      best = closerPair(best, nearestPointOnSegment(corner, a, b))
    }
    // A segment cutting straight through with both endpoints outside
    // evades the vertex candidates — the true distance there is 0.
    if (best.distance > 0 && segmentIntersectsBox(a, b, box)) return OVERLAPPED
  }
  return awayFromLine(best)
}

const closerPair = (a: NearestPair, b: NearestPair): NearestPair =>
  b.distance < a.distance ? b : a

// Segment endpoint vs the box: the nearest box point is the clamp.
const nearestEndpointToBox = (box: PlanLetterBox, [px, py]: [number, number]): NearestPair => {
  const cx = Math.min(Math.max(px, box.left), box.right)
  const cy = Math.min(Math.max(py, box.top), box.bottom)
  return { distance: Math.hypot(px - cx, py - cy), boxX: cx, boxY: cy, lineX: px, lineY: py }
}

const boxCorners = (box: PlanLetterBox): Array<[number, number]> => [
  [box.left, box.top],
  [box.right, box.top],
  [box.left, box.bottom],
  [box.right, box.bottom],
]

const nearestPointOnSegment = (
  [px, py]: [number, number],
  [ax, ay]: [number, number],
  [bx, by]: [number, number],
): NearestPair => {
  const abx = bx - ax
  const aby = by - ay
  const lengthSquared = abx * abx + aby * aby
  const t = lengthSquared === 0
    ? 0
    : Math.min(1, Math.max(0, ((px - ax) * abx + (py - ay) * aby) / lengthSquared))
  const lx = ax + abx * t
  const ly = ay + aby * t
  return { distance: Math.hypot(px - lx, py - ly), boxX: px, boxY: py, lineX: lx, lineY: ly }
}

const awayFromLine = (best: NearestPair): { distance: number; awayX: number; awayY: number } => {
  if (best.distance <= 1e-6) return OVERLAPPED
  return {
    distance: best.distance,
    awayX: (best.boxX - best.lineX) / best.distance,
    awayY: (best.boxY - best.lineY) / best.distance,
  }
}

// Liang-Barsky segment-vs-box hit test.
const segmentIntersectsBox = (
  [ax, ay]: [number, number],
  [bx, by]: [number, number],
  box: PlanLetterBox,
): boolean => {
  const range = { t0: 0, t1: 1 }
  const clips: Array<[number, number]> = [
    [-(bx - ax), ax - box.left],
    [bx - ax, box.right - ax],
    [-(by - ay), ay - box.top],
    [by - ay, box.bottom - ay],
  ]
  for (const [p, q] of clips) {
    if (!clipAgainstEdge(p, q, range)) return false
  }
  return range.t0 <= range.t1
}

// One Liang-Barsky clip: narrows [t0,t1] or rejects the segment.
const clipAgainstEdge = (p: number, q: number, range: { t0: number; t1: number }): boolean => {
  if (p === 0) return q >= 0
  const r = q / p
  if (p < 0) {
    if (r > range.t1) return false
    if (r > range.t0) range.t0 = r
  } else {
    if (r < range.t0) return false
    if (r < range.t1) range.t1 = r
  }
  return true
}
